import Button from "../Common/Button";
import Input from "../Common/Input";
import Label from "../Common/Label";
import ModelImage from "../../assets/images/CreateZoneModalImage.png"
import { useContext, useEffect, useRef, useState } from "react";
import cityTimezones from 'city-timezones';
import ToastMessage from "../Common/ToastMessage";
import InputDropdown from "../Common/InputDropdown";
import CityInputDropdown from "../Common/CityInputDropdown";
import OpenWeatherMapContext from "../../contexts/OpenWeatherMapContext";
import openWeatherMap from "../../lib/openweathermap";
import { useIsWriteRestrictedUser } from "../../hooks/useIsWriteRestrictedUser";

const TIME_ZONES = [
  "America/Adak",
  "America/Anchorage",
  "America/Boise",
  "America/Chicago",
  "America/Denver",
  "America/Detroit",
  "America/Indiana/Indianapolis",
  "America/Juneau",
  "America/Los_Angeles",
  "America/New_York",
  "America/Nome",
  "America/Phoenix",
  "America/Puerto_Rico",
  "America/Sitka",
  "Pacific/Guam",
  "Pacific/Honolulu",
  "Pacific/Pago_Pago"
];

export default function BasePodModal({ initialFormData, apiMethod, title, buttonText, shouldFocusOnNameField, hideModal, additionalValidationFunc, onSuccess }) {
  const inputZoneRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    weatherWidgetId: '',
    cityName: '',
    timeZone: '',
    ...(initialFormData || {}),
  });
  const [isFormValid, setIsFormValid] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);
  const [cityNames, setCityNames] = useState([]);
  const cityFetchTimeout = useRef(null);
  const [errors, setErrors] = useState({});
  const {
    apiKey: openWeatherMapApiKey,
    cityIdApiKey: openWeatherMapCityIdApiKey,
  } = useContext(OpenWeatherMapContext);
  const isActionsDisabled = useIsWriteRestrictedUser();

  const [toast, setToast] = useState({
    show: false,
    message: '',
    type: 'success',
  });
  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
  };

  const [filteredTimezones, setFilteredTimezones] = useState(TIME_ZONES);

  const fetchCityNames = async (query) => {
    try {
      setLoadingCities(true);
      const geoCodeResults = await openWeatherMap.tryGeocode(
        openWeatherMapApiKey, query
      );

      setCityNames((geoCodeResults || []).map((city) => {
        const parts = [city.name];
        if (city.state) parts.push(city.state);
        if (city.country) parts.push(city.country);
        return {
          id: parts.join(', '),
          name: parts.join(', '),
        };
      }));
    } catch (err) {
      console.error("City fetch error:", err);
      setCityNames([]);
    } finally {
      setLoadingCities(false);
    }
  };

  const fetchCityWeatherWidgetId = async (cityName) => {
    try {
      setLoadingCities(true);
      const weatherWidgetId = await openWeatherMap.fetchCityId(
        openWeatherMapCityIdApiKey, cityName
      );
      setFormData(prev => ({
        ...prev,
        weatherWidgetId,
      }));
    } catch (err) {
      showToast(err.message || 'Network error. Please try again', 'failed');
      console.error("City id fetch error:", err);
      setFormData(prev => ({
        ...prev,
        weatherWidgetId: '',
        cityName: '',
      }));
    } finally {
      setLoadingCities(false);
    }
  }

  useEffect(() => {
    if (shouldFocusOnNameField && inputZoneRef.current) {
      inputZoneRef.current.focus();
    }
  }, [shouldFocusOnNameField]);


  const handleChange = (e, source = "input", selectedOption = null) => {
    const { name, value } = e.target;

    setFormData((prev) => {
      let updatedForm = {
        ...prev,
        [name]: value,
      };

      if (name === "cityName") {
        if (source === "dropdown" && selectedOption) {
          fetchCityWeatherWidgetId(selectedOption.name)

          const cityParts = selectedOption.name.split(",");
          const city = cityParts[0]?.trim();
          const country = cityParts[cityParts.length - 1]?.trim();

          const cityLookup = cityTimezones.lookupViaCity(city);
          let matchedCity = cityLookup.find(
            (item) =>
              item.city.toLowerCase() === city.toLowerCase() &&
              item.country.toLowerCase() === country.toLowerCase()
          );

          if (!matchedCity) {
            matchedCity = cityLookup.find(
              (item) =>
                item.city.toLowerCase() === city.toLowerCase() &&
                (item.country.toLowerCase() === "united states of america" ||
                  item.iso2?.toLowerCase() === "us")
            );
          }

          if (!matchedCity && cityLookup.length > 0) {
            matchedCity = cityLookup[0];
          }

          if (matchedCity) {
            updatedForm.timeZone = matchedCity.timezone;
            setErrors((prev) => ({ ...prev, timeZone: "" }));
          }
        } else {
          // user typing
          if (cityFetchTimeout.current) clearTimeout(cityFetchTimeout.current);

          if (typeof value === "string" && value.trim().length >= 1) {
            cityFetchTimeout.current = setTimeout(() => {
              fetchCityNames(value.trim());
            }, 300);
          } else {
            setCityNames([]);
          }

          updatedForm.weatherWidgetId = ""; // clear id
        }
        setErrors(prev => ({ ...prev, city: "" }));
      }

      if (name === "name") {
        if (value.length > 35) {
          setErrors((prev) => ({
            ...prev,
            name: "Zone name cannot exceed more than 35 characters",
          }));
          return prev; // ❌ keep previous state, don’t update
        } else {
          setErrors((prev) => ({ ...prev, name: "" }));
        }
      }

      if (name === "timeZone") {
        if (value.trim() === "") {
          setErrors((prev) => ({ ...prev, timeZone: "Time Zone is required." }));
        } else {
          setErrors((prev) => ({ ...prev, timeZone: "" }));
        }

        const filtered = TIME_ZONES.filter((tz) =>
          tz.toLowerCase().includes(value.toLowerCase())
        );
        setFilteredTimezones(filtered);
      }

      return updatedForm;
    });
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name?.trim()) newErrors.name = 'Name is required';
    if (!formData.weatherWidgetId?.trim()) newErrors.cityName = 'Invalid City Name';
    if (!formData.cityName?.trim()) newErrors.cityName = 'cityName is required';
    if (!formData.timeZone?.trim()) newErrors.timeZone = 'Time Zone is required.';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isActionsDisabled) return;

    if (!validate()) return;

    const payload = {
      ...formData,
      name: formData.name?.trim(),
    };

    try {
      const res = await apiMethod(payload);
      if (res.status === 200) {
        hideModal();
        onSuccess(res.data?.zone);
      } else {
        if (res.errorCode === "FMS_ZONE_NAME_TAKEN") {
          showToast("Zone name already taken", 'failed');
        } else {
          showToast(res.message, 'failed');
        }
        console.log(res.message);
      }
    } catch (err) {
      showToast('Network error. Please try again', 'failed');
    }
  };

  useEffect(() => {
    let additionalValidatorResults = {isValid: true, errors: {}};
    if (typeof additionalValidationFunc === "function") {
      additionalValidatorResults = additionalValidationFunc(formData);
      if (Object.keys(additionalValidatorResults.errors).some(k => !errors[k] || errors[k] !== additionalValidatorResults.errors[k])) {
        setErrors((prev) => ({
          ...prev,
          ...additionalValidatorResults.errors,
        }));
      }
    }
    setIsFormValid(
      (formData.name || '').length > 0 &&
      (formData.weatherWidgetId || '').length > 0 &&
      (formData.cityName || '').length > 0 &&
      (formData.timeZone || '').length > 0 &&
      Object.values(errors).filter(error => error &&  error.length > 0).length === 0 &&
      additionalValidatorResults.isValid
    );
  }, [formData, errors, additionalValidationFunc]);

  return (
    <>
      <div className="overlayHolder bg-black/30 dark:bg-black/60 fixed inset-0 flex justify-center z-[105] h-full" data-testid="pod-modal">
        <div className="modalHolder bg-ivory dark:bg-darkThemeModalBg rounded-2xl border border-black/10 w-full sm:w-4/5 md:w-3/5 mx-5 lg:w-[850px]  max-h-[90%] sm:max-h-[75%] lg:max-h-[600px] my-auto p-10 lg:p-14 overflow-auto dark:shadow-darkThemeModalShadow">
          <div className="flex gap-10 lg:gap-14">
            <form className="modalContent basis-full lg:basis-[300px]">
              <div className="flex flex-col gap-1">
                <h2 className="modalTitle text-[#031641] dark:text-white text-xl leading-6 lg:text-2xl lg:leading-7 font-semibold tracking-[0.036px]" data-testid="pod-modal-title">{title}</h2>
                <p className="text-[#000000DE] dark:text-white text-sm leading-5 tracking-[0.021px] font-normal w-full lg:w-11/12">Group your Gradient units for centralized control and monitoring.</p>
              </div>
              <div className="modalFormHolder mt-8">
                <div className="space-y-5 lg:space-y-7">
                  <div className="podNameSection gap-1 flex flex-col">
                    <div className="flex flex-col gap-1 relative">
                      <Label
                        htmlFor="name"
                        className="podNameLabel text-[#00000099] dark:text-white font-normal text-base leading-5 tracking-[-0.24px]"
                      >
                        <>
                          Zone Name <span className="text-theme">*</span>
                        </>
                      </Label>
                      <Input
                        id="name"
                        name="name"
                        ref={inputZoneRef}
                        maxLength={35}
                        type="text"
                        value={formData.name ?? ""}
                        placeholder="Enter Zone Name"
                        onChange={handleChange}
                        className="w-full text-lg leading-6 py-2.5 px-3 rounded-md border border-[#CADBD5] text-dark-grey h-11 bg-white dark:darkThemeGradiantBg dark:text-white dark:border-none placeholder:text-dark-grey/40 dark:placeholder:text-white/40 font-medium tracking-[-0.408px] outline-none"
                      />
                      {errors.name && (
                        <p className='text-critical text-xs leading-4' data-testid="pod-modal-name-error">
                          {errors.name}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="zipCodeSection gap-1 flex flex-col">
                    <div className="flex flex-col gap-1 relative">
                      <CityInputDropdown
                        label="City Name"
                        name="cityName"
                        id="cityName"
                        value={formData.cityName}
                        onChange={handleChange}
                        loading={loadingCities}
                        placeholder="Enter City Name"
                        className="w-full text-lg leading-6 py-2.5 px-3 rounded-md border border-[#CADBD5] text-dark-grey h-11 bg-white dark:darkThemeGradiantBg dark:text-white dark:border-none placeholder:text-dark-grey/40 dark:placeholder:text-white/40 font-medium tracking-[-0.408px] outline-none"
                        labelClass="timeZoneLabel text-[#00000099] dark:text-white font-normal text-base leading-5 tracking-[-0.24px]"
                        options={cityNames}
                      />
                      {errors.city && (
                        <p className='text-critical text-xs leading-4' data-testid="pod-modal-city-error">
                          {errors.city}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="timeZoneSection gap-1 flex flex-col relative">
                    <InputDropdown
                      label="Time Zone"
                      name="timeZone"
                      id="timeZone"
                      value={formData.timeZone}
                      onChange={handleChange}
                      placeholder="Enter Time Zone"
                      className="w-full text-lg leading-6 py-2.5 px-3 rounded-md border border-[#CADBD5] text-dark-grey h-11 bg-white dark:darkThemeGradiantBg dark:text-white dark:border-none placeholder:text-dark-grey/40 dark:placeholder:text-white/40 font-medium tracking-[-0.408px] outline-none"
                      labelClass="timeZoneLabel text-[#00000099] dark:text-white font-normal text-base leading-5 tracking-[-0.24px]"
                      options={filteredTimezones}
                    />
                    {errors.timeZone && (
                      <p className="text-critical text-xs leading-4" data-testid="pod-modal-timezone-error">
                        {errors.timeZone}
                      </p>
                    )}
                  </div>
                </div>
              </div>
              <div className="mt-10 actionsHolder">
                <div className="flex items-center flex-wrap lg:flex-nowrap gap-4 lg:gap-6">
                  <Button
                    className={`flex justify-center items-center basis-[140px] shrink max-[450px]:grow grow-0 bg-theme px-5 py-3 text-white text-base font-semibold leading-5 tracking-[-0.32px] rounded-md h-10 hover:bg-theme/80 transition-all duration-300 ease-in-out ${
                      !isFormValid || isActionsDisabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
                    }`}
                    onClick={handleSubmit}
                    disabled={!isFormValid || isActionsDisabled}
                    data-testid="pod-modal-submit"
                  >
                    {buttonText}
                  </Button>
                  <Button
                    className="flex justify-center items-center basis-[140px] shrink max-[450px]:grow grow-0  border border-theme px-5 py-3 text-theme text-base font-semibold leading-5 tracking-[-0.32px] rounded-md cursor-pointer h-10 hover:bg-theme hover:text-white transition-all duration-300 ease-in-out"
                    onClick={hideModal}
                    data-testid="pod-modal-cancel"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </form>
            <div className="modalImageHolder hidden lg:flex">
              <img src={ModelImage} className="w-full h-full object-cover dark:rounded-lg" alt="modalImage" />
            </div>
          </div>
        </div>
      </div>

      <ToastMessage
        message={toast.message}
        type={toast.type}
        show={toast.show}
        onClose={() => setToast(prev => ({ ...prev, show: false }))}
      />
    </>
  );
}
